import React from "react";
import { StyledFileDropZone } from "./MediaManager.style";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { MEDIA_TYPE } from "constants/global";
import { findMediaInfoByFileName } from "utils/media";

const MAX_AUDIO_VIDEO_SIZE = 5 * 1024 * 1024 * 1024;
const MAX_OTHER_FILE_SIZE = 512 * 1024 * 1024;

const validateFileSize = (file) => {
  const { type } = findMediaInfoByFileName(file.name);
  const isAudioOrVideo = type === MEDIA_TYPE.AUDIO || type === MEDIA_TYPE.VIDEO;
  const maxSize = isAudioOrVideo ? MAX_AUDIO_VIDEO_SIZE : MAX_OTHER_FILE_SIZE;

  if (file.size > maxSize) {
    return {
      code: "file-too-large",
      message: `${isAudioOrVideo ? "Audio and video" : "Other"} files must be ${
        isAudioOrVideo ? "5 GB" : "512 MB"
      } or smaller`,
    };
  }

  return null;
};

const MediaDropZone = ({ onDrop }) => {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
  } = useDropzone({
    onDrop,
    validator: validateFileSize,
    multiple: true
  });

  const getDropZoneContent = () => {
    if (isDragReject) {
      return (
        <Box sx={{ textAlign: 'center', color: 'error.main' }}>
          <CloudUploadIcon sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
          <Typography variant="h6" component="p" sx={{ mb: 1 }}>
            Some files are not supported
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Check the file type and size limits
          </Typography>
        </Box>
      );
    }

    if (isDragActive) {
      return (
        <Box sx={{ textAlign: 'center', color: 'primary.main' }}>
          <CloudUploadIcon sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
          <Typography variant="h6" component="p" sx={{ mb: 1 }}>
            Drop files here...
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Release to upload your files
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center' }}>
        <CloudUploadIcon sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
        <Typography variant="h6" component="p" sx={{ mb: 1 }}>
          Drag & drop files here, or click to select
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
          Upload images, videos, audio, documents and more
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          <Chip label="Images" size="small" variant="outlined" />
          <Chip label="Videos" size="small" variant="outlined" />
          <Chip label="Audio" size="small" variant="outlined" />
          <Chip label="Documents" size="small" variant="outlined" />
        </Box>
        <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.6 }}>
          Audio and video: 5 GB maximum • Other files: 512 MB maximum
        </Typography>
      </Box>
    );
  };

  return (
    <StyledFileDropZone 
      {...getRootProps()}
      className={`
        ${isDragActive ? 'drag-active' : ''}
        ${isDragReject ? 'drag-reject' : ''}
      `}
    >
      <input {...getInputProps()} />
      {getDropZoneContent()}
    </StyledFileDropZone>
  );
};

export default MediaDropZone;
